import { formatCNPJ, formatCPF, formatDate, formatPhone, plural } from '@westhetics/ui'
import type {
  Audience,
  Completeness,
  DocType,
  DuplicateMatchKey,
  ForeignDocumentType,
  Gender,
  ImageConsent,
  MaritalStatus,
  Patient,
  ReferralChannel,
} from './api'

/** Rótulos do cadastro de paciente: o valor do contrato nunca vai cru para a tela (T21). */

/** Os públicos da pessoa, na ordem do contrato (RN-PAC-36). */
export const AUDIENCES: Audience[] = ['patient', 'student', 'employee', 'third_party']

/** O público no plural, como no segmento da lista e no título: "Alunos". */
export const AUDIENCE_LABELS: Record<Audience, string> = {
  patient: 'Pacientes',
  student: 'Alunos',
  employee: 'Funcionários',
  third_party: 'Terceiros',
}

/** Como a tela chama a pessoa de cada público (RN-PAC-40): nas frases, no título e no documento. */
export interface PersonWords {
  /** "paciente", "aluno": dentro da frase. */
  one: string
  /** "pacientes", "alunos". */
  many: string
  /** "Paciente", "Aluno": eyebrow e opção do campo Público. */
  title: string
  /** "Pacientes", "Alunos": título da lista e migalha. */
  titleMany: string
  /** "CPF" para paciente; "CPF ou CNPJ" para quem pode ter CNPJ (RN-PAC-38). */
  document: string
}

const PERSON_WORDS: Record<Audience, [string, string]> = {
  patient: ['paciente', 'pacientes'],
  student: ['aluno', 'alunos'],
  employee: ['funcionário', 'funcionários'],
  third_party: ['terceiro', 'terceiros'],
}

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

export function personWords(audience: Audience = 'patient'): PersonWords {
  const [one, many] = PERSON_WORDS[audience]
  return {
    one,
    many,
    title: capitalize(one),
    titleMany: AUDIENCE_LABELS[audience],
    document: audience === 'patient' ? 'CPF' : 'CPF ou CNPJ',
  }
}

/** O documento com a máscara do tipo: "529.982.247-25" ou "12.ABC.345/01DE-35". */
export function formatDocument(docNumber: string, docType: DocType | null): string {
  return docType === 'cnpj' ? formatCNPJ(docNumber) : formatCPF(docNumber)
}

export const MARITAL_STATUSES: MaritalStatus[] = [
  'single',
  'married',
  'domestic_partnership',
  'separated',
  'divorced',
  'widowed',
]

export const MARITAL_STATUS_LABELS: Record<MaritalStatus, string> = {
  single: 'Solteira(o)',
  married: 'Casada(o)',
  domestic_partnership: 'União estável',
  separated: 'Separada(o)',
  divorced: 'Divorciada(o)',
  widowed: 'Viúva(o)',
}

export const GENDERS: Gender[] = ['not_provided', 'female', 'male', 'other']

export const GENDER_LABELS: Record<Gender, string> = {
  female: 'Feminino',
  male: 'Masculino',
  other: 'Outro',
  not_provided: 'Não informado',
}

export const IMAGE_CONSENTS: ImageConsent[] = ['yes', 'no', 'not_provided']

/** "Não informado" nunca vale como "não" (RN-PAC-20). */
export const IMAGE_CONSENT_LABELS: Record<ImageConsent, string> = {
  yes: 'Autoriza o uso de imagem',
  no: 'Não autoriza o uso de imagem',
  not_provided: 'Não informado',
}

export const REFERRAL_CHANNELS: ReferralChannel[] = [
  'referral',
  'social_media',
  'instagram',
  'google',
  'ad',
  'website',
  'storefront',
  'event',
  'other',
]

export const REFERRAL_CHANNEL_LABELS: Record<ReferralChannel, string> = {
  referral: 'Indicação',
  social_media: 'Redes sociais',
  instagram: 'Instagram',
  google: 'Google',
  ad: 'Anúncio',
  website: 'Site',
  storefront: 'Fachada da clínica',
  event: 'Evento',
  other: 'Outro',
}

export const FOREIGN_DOCUMENT_TYPES: ForeignDocumentType[] = ['passport', 'rne', 'crnm', 'other']

export const FOREIGN_DOCUMENT_TYPE_LABELS: Record<ForeignDocumentType, string> = {
  passport: 'Passaporte',
  rne: 'RNE',
  crnm: 'CRNM',
  other: 'Outro documento',
}

/** Por que dois cadastros foram agrupados (RN-PAC-16), como entra no meio de uma frase. */
export const MATCH_KEY_LABELS: Record<DuplicateMatchKey, string> = {
  docNumber: 'mesmo CPF',
  foreignDocument: 'mesmo documento estrangeiro',
  whatsapp: 'mesmo WhatsApp',
}

/** "Mesmo CPF": o motivo do grupo como rótulo. */
export function matchKeyTitle(key: DuplicateMatchKey) {
  const label = MATCH_KEY_LABELS[key]
  return label.charAt(0).toUpperCase() + label.slice(1)
}

/** Os DDIs oferecidos no cadastro; o Brasil é o padrão (RT-17). */
export const DIAL_CODES: { code: string; label: string }[] = [
  { code: '+55', label: 'Brasil +55' },
  { code: '+54', label: 'Argentina +54' },
  { code: '+591', label: 'Bolívia +591' },
  { code: '+56', label: 'Chile +56' },
  { code: '+57', label: 'Colômbia +57' },
  { code: '+595', label: 'Paraguai +595' },
  { code: '+51', label: 'Peru +51' },
  { code: '+598', label: 'Uruguai +598' },
  { code: '+1', label: 'Estados Unidos e Canadá +1' },
  { code: '+52', label: 'México +52' },
  { code: '+351', label: 'Portugal +351' },
  { code: '+34', label: 'Espanha +34' },
  { code: '+33', label: 'França +33' },
  { code: '+39', label: 'Itália +39' },
  { code: '+49', label: 'Alemanha +49' },
  { code: '+44', label: 'Reino Unido +44' },
  { code: '+41', label: 'Suíça +41' },
  { code: '+81', label: 'Japão +81' },
]

export const DEFAULT_DIAL_CODE = '+55'

const onlyDigits = (text: string) => text.replace(/\D/g, '')

/** Separa o E.164 guardado em DDI e número, pelo DDI mais longo que a lista conhece. */
export function splitWhatsapp(whatsapp: string | null): { dialCode: string; national: string } {
  const digits = onlyDigits(whatsapp ?? '')
  if (digits === '') return { dialCode: DEFAULT_DIAL_CODE, national: '' }
  const match = [...DIAL_CODES]
    .sort((a, b) => b.code.length - a.code.length)
    .find(({ code }) => digits.startsWith(code.slice(1)))
  if (!match) return { dialCode: DEFAULT_DIAL_CODE, national: digits }
  return { dialCode: match.code, national: digits.slice(match.code.length - 1) }
}

/** O WhatsApp como a pessoa lê: sem o +55 do Brasil ("67 99686-0918"); de fora, com o DDI. */
export function displayWhatsapp(whatsapp: string | null): string {
  if (!whatsapp) return ''
  const { dialCode, national } = splitWhatsapp(whatsapp)
  return dialCode === DEFAULT_DIAL_CODE ? formatPhone(national) : `${dialCode} ${national}`
}

export { isoToBrDate } from '@/lib/dates'

/** O nome de exibição do país a partir do código ISO alfa-2. */
const regionNames = new Intl.DisplayNames('pt-BR', { type: 'region' })

/** ISO 3166-1 alfa-2. O nome sai do `Intl`, em português. */
const COUNTRY_CODES =
  'AD AE AF AG AI AL AM AO AR AS AT AU AW AZ BA BB BD BE BF BG BH BI BJ BM BN BO BR BS BT BW BY BZ CA CD CF CG CH CI CL CM CN CO CR CU CV CW CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FM FR GA GB GD GE GF GH GM GN GP GQ GR GT GW GY HK HN HR HT HU ID IE IL IN IQ IR IS IT JM JO JP KE KG KH KM KN KP KR KW KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MG MK ML MM MN MO MQ MR MT MU MV MW MX MY MZ NA NE NG NI NL NO NP NZ OM PA PE PG PH PK PL PR PS PT PY QA RE RO RS RU RW SA SB SC SD SE SG SI SK SL SM SN SO SR SS ST SV SY SZ TD TG TH TJ TL TM TN TO TR TT TW TZ UA UG US UY UZ VA VC VE VN VU WS YE ZA ZM ZW'

export function countryName(code: string): string {
  try {
    return regionNames.of(code.toUpperCase()) ?? code
  } catch {
    return code
  }
}

/** Os países em ordem alfabética do nome em português. */
export const COUNTRIES: { code: string; name: string }[] = COUNTRY_CODES.split(' ')
  .map((code) => ({ code, name: countryName(code) }))
  .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))

/** "CPF" e "CPF ou CNPJ" ficam como estão; "E-mail" entra na frase em minúscula ("falta e-mail"). */
function inSentence(label: string) {
  const [first = ''] = label.split(' ')
  return first === first.toUpperCase() ? label : label.charAt(0).toLowerCase() + label.slice(1)
}

/**
 * A frase do medidor (RN-PAC-09): "Cadastro 86% completo — falta CPF." O único lugar do sistema
 * onde uma porcentagem aparece, porque vem com a consequência escrita.
 */
export function completenessSentence(completeness: Completeness): string {
  const [first, ...others] = completeness.missing
  if (!first) return `Cadastro ${completeness.percent}% completo.`
  const rest = others.length > 0 ? ` e mais ${plural(others.length, 'item', 'itens')}` : ''
  return `Cadastro ${completeness.percent}% completo — falta ${inSentence(first.label)}${rest}.`
}

/** Quantas pendências dependem da clínica: `waiting` aparece, mas não conta (RN-PAC-10). */
export function clinicPendingCount(patient: Patient): number {
  return patient.pendingItems.items.filter((item) => item.level !== 'waiting').length
}

/**
 * A linha de identificação da ficha: "binha_deeh@hotmail.com · 33 anos · autônoma · São Paulo ·
 * paciente desde 2025". Só entra o que existe; quem se cadastrou pelo link da clínica diz quando
 * (RN-PAC-34).
 */
export function identificationLine(patient: Patient): string {
  const parts: string[] = []
  if (patient.email) parts.push(patient.email)
  if (patient.age !== null) parts.push(patient.age === 1 ? '1 ano' : `${patient.age} anos`)
  if (patient.occupation) parts.push(patient.occupation.toLocaleLowerCase('pt-BR'))
  if (patient.address?.city) parts.push(patient.address.city)
  parts.push(`${personWords(patient.audience).one} desde ${patient.patientSince.slice(0, 4)}`)
  if (patient.selfRegisteredAt) {
    parts.push(
      `cadastro feito pela paciente pelo link em ${formatDate(new Date(patient.selfRegisteredAt))}`,
    )
  }
  return parts.join(' · ')
}

/** "Endereço e Contato" · "3 anamneses, 5 documentos e 7 planos". */
export function joinList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? ''
  return `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]!}`
}
